/** Shared protocol constants of the netcode (docs/NETCODE.md). */

/**
 * `gr_core::consts::TICK_RATE`: simulation and snapshot rate (a snapshot every tick). Every duration
 * the client counts in ticks or snapshots is defined in seconds (`ticks` / `wholeTicks`), so it
 * means the same time at any rate.
 */
export const TICK_RATE = 120;
/** Seconds per simulation tick. */
export const TICK_DT = 1 / TICK_RATE;
/** Milliseconds per simulation tick. */
export const TICK_MS = 1000 / TICK_RATE;

/** `secs` seconds in ticks, fractional (`gr_core::consts::ticks`). */
export function ticks(secs: number): number {
  return secs * TICK_RATE;
}

/** `secs` seconds in whole ticks, rounded to the nearest tick (`gr_core::consts::whole_ticks`). */
export function wholeTicks(secs: number): number {
  return Math.round(ticks(secs));
}

/** `gr_core::consts::PROTOCOL_VERSION`; sent in `ClientMessage.hello`. */
export const PROTOCOL_VERSION = 5;

/** `gr_core::protocol::SLACK_PER_TICK`: `Snapshot.inputSlack` is in 1/960 s at any tick rate. */
export const SLACK_PER_TICK = 960 / TICK_RATE;
/** `gr_core::protocol::MAX_INPUTS_PER_PACKET`: newest inputs per packet (133 ms of ticks). */
export const MAX_INPUTS_PER_PACKET = 16;

/** Ping/pong period on the unreliable channel. */
export const PING_INTERVAL_MS = 250;
/** Start the WebSocket attempt if WebTransport is not ready after this long. */
export const WT_FALLBACK_MS = 3000;
/** The server drops a connection that sends nothing for 10 s; the client does the same. */
export const IDLE_TIMEOUT_MS = 10_000;
/** Largest reliable frame accepted from the server (protocol limit 16 KiB, with slack). */
export const MAX_RELIABLE_FRAME = 256 * 1024;

/**
 * Sim ticks per rendered frame (133 ms) before the prediction clock gives up and resyncs: as many
 * as one input packet carries, so every tick of a frame reaches the server.
 */
export const MAX_TICKS_PER_FRAME = MAX_INPUTS_PER_PACKET;
/** Jitter (ticks; 5 ms) the lead controller assumes until it has measured arrivals (`gr_core::lead::SEED_PEAK_TICKS`). */
export const LEAD_SEED_PEAK_TICKS = ticks(0.005);
/** Snapshots averaged for the net graph's input lead (1/6 s). */
export const LEAD_WINDOW = wholeTicks(1 / 6);
